import { useEffect } from "react";
import { ServiceDetailViewModel, ServiceTypeEnum } from "@payping/api";
import { Delete, RedoOutlined, Wrench } from "@payping/icons";
import { createService, deleteService, getServiceList, updateService } from "@payping/redux/actions";
import {
  Button,
  Checkbox,
  Form,
  Input,
  Message,
  Space,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";

import { servicesAsPluginList } from "../data";

interface Props {
  plugin: (typeof servicesAsPluginList)[keyof typeof servicesAsPluginList];
}

interface MessageFormType {
  apiKey?: string;
  getPayNotify?: boolean;
  getImportantNotifies?: boolean;
  hookUrl?: string;
  merchantId?: number;
  phoneNumber?: string;
  sendDescription?: string;
  sendReciept?: boolean;
}

const Messaging = ({ plugin }: Props) => {
  const { t } = useAppTranslation();
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const serviceList = useAppSelector((state) => state.service.list);
  const serviceDetails = serviceList.find((s) => s.serviceType === plugin.serviceType);

  useEffect(() => {
    if (serviceDetails) {
      const { apiKey, getImportantNotifies, getPayNotify, phoneNumber } = serviceDetails;
      form.setFieldsValue({ apiKey, getImportantNotifies, getPayNotify, phoneNumber });
    }
  }, [form, serviceDetails]);

  useMessagedAsyncRetry(async () => dispatch(getServiceList()), [], { useProgress: true });

  const [, handleCreate] = useMessagedAsyncFn(
    async (values: MessageFormType) => {
      const response = await dispatch(createService({ ...values, serviceType: plugin.serviceType }));
      Message.success({ content: t("services.detail.kavenegar.message.successCreate") });
      return response;
    },
    [],
    undefined,
    { useProgress: true },
  );

  const [, handleEdit] = useMessagedAsyncFn(
    async (data: ServiceDetailViewModel) => {
      const response = await dispatch(updateService(data));
      Message.success({ content: t("services.detail.kavenegar.message.successEdit") });
      return response;
    },
    [],
    undefined,
    { useProgress: true },
  );

  const [, handleDelete] = useMessagedAsyncFn(
    async (data: ServiceDetailViewModel) => {
      const response = await dispatch(deleteService(data));
      form.resetFields();
      Message.success({ content: t("services.detail.kavenegar.message.successDelete") });
      return response;
    },
    [],
    undefined,
    { useProgress: true },
  );

  const inputItems = (serviceType: number) => {
    switch (serviceType) {
      case ServiceTypeEnum.Slack: // Slack
        return (
          <Form.Item label={t("services.detail.messaging.codePrefix", { name: "webhook" })} name={"hookUrl"}>
            <Input />
          </Form.Item>
        );
      case ServiceTypeEnum.KavehNegar: // KavehNegar
        return (
          <>
            <Form.Item
              label={t("services.detail.messaging.codePrefix", { name: "apiKey" })}
              required
              rules={[{ required: true }]}
              name={"apiKey"}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t("services.detail.messaging.kavehNegarNumber")}
              required
              rules={[{ required: true }]}
              name={"phoneNumber"}
            >
              <Input />
            </Form.Item>
          </>
        );
      case ServiceTypeEnum.MailerLite: // MailerLite
        return (
          <Form.Item
            label={t("services.detail.messaging.codePrefix", { name: "apiKey" })}
            required
            rules={[{ required: true }]}
            name={"apiKey"}
          >
            <Input />
          </Form.Item>
        );
      case ServiceTypeEnum.Telegram: // Telegram
        return null;
      default:
        return null;
    }
  };

  return (
    <Form
      form={form}
      onFinish={(values: MessageFormType) => {
        const data = {
          ...values,
          phoneNumber: String(values.phoneNumber),
          serviceType: plugin.serviceType,
        };
        if (serviceDetails) {
          handleEdit(data);
        } else if (data.serviceType === ServiceTypeEnum.Telegram) {
          const newWindow = window.open(undefined, "_blank");
          handleCreate(data).then((result) => newWindow?.location?.assign?.(result));
        } else {
          handleCreate(data);
        }
      }}
      layout={"vertical"}
    >
      {inputItems(plugin.serviceType)}
      <Form.Item name={"getPayNotify"} valuePropName={"checked"}>
        <Checkbox>{t("services.detail.messaging.reportMessage")}</Checkbox>
      </Form.Item>
      <Form.Item name={"getImportantNotifies"} valuePropName={"checked"}>
        <Checkbox>{t("services.detail.messaging.specialMessage")}</Checkbox>
      </Form.Item>
      <Space>
        <Button
          htmlType="submit"
          icon={serviceDetails ? <RedoOutlined width={14} height={14} /> : <Wrench width={14} height={14} />}
          type={"primary"}
        >
          {t(serviceDetails ? "update" : "install")}
        </Button>
        {serviceDetails ? (
          <Button
            icon={<Delete width={14} height={14} />}
            type={"default"}
            onClick={() => handleDelete({ serviceType: plugin.serviceType })}
          >
            {t("delete")}
          </Button>
        ) : null}
      </Space>
    </Form>
  );
};

export default Messaging;
